import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import PoPrintTemplate
    from "../Accounts/PoPrintTemplate";

const PoRevisionPrintTemplate = ({
    printHistory
}) => {


    const theme = localStorage.getItem("theme");
    const isDark = theme === "dark";
    // ==========================================
    // SORT ALL PO VERSIONS
    // Original → Revision 1 → Revision 2 → Current
    // ==========================================

    const versions = useMemo(() => {
        const versionList =
            Array.isArray(
                printHistory?.versions
            )
                ? printHistory.versions
                : [];

        return [...versionList].sort(
            (a, b) =>
                Number(
                    a?.revisionNo || 0
                ) -
                Number(
                    b?.revisionNo || 0
                )
        );
    }, [printHistory]);

    // ==========================================
    // FIND CURRENT / LATEST VERSION
    // ==========================================

    const currentVersion = useMemo(() => {
        return (
            versions.find(
                (version) =>
                    version?.isCurrent
            ) ||
            versions[
            versions.length - 1
            ] ||
            null
        );
    }, [versions]);

    const [
        selectedRevisionNo,
        setSelectedRevisionNo
    ] = useState(null);

    // ==========================================
    // DEFAULT SELECT CURRENT PO
    // ==========================================

    useEffect(() => {
        if (!versions.length) {
            setSelectedRevisionNo(null);
            return;
        }

        setSelectedRevisionNo(
            currentVersion?.revisionNo ??
            versions[
                versions.length - 1
            ]?.revisionNo ??
            0
        );
    }, [
        versions,
        currentVersion?.revisionNo
    ]);

    if (!versions.length) {
        return (
            <div
                className="
                    text-center
                    text-muted
                    p-4
                "
            >
                No Purchase Order versions found.
            </div>
        );
    }

    // ==========================================
    // SELECTED VERSION
    // ==========================================

    const selectedVersion =
        versions.find(
            (version) =>
                Number(
                    version?.revisionNo
                ) ===
                Number(
                    selectedRevisionNo
                )
        ) ||
        currentVersion ||
        versions[0];

    // ==========================================
    // BUILD DATA FOR EXISTING PO PRINT TEMPLATE
    // ==========================================

    const selectedPo = {
        /*
         * Keep common/latest populated PO data.
         */
        ...(printHistory || {}),

        /*
         * Apply the selected original/revision snapshot.
         */
        ...(selectedVersion?.po || {}),

        // ==========================================
        // SELECTED VERSION BASIC DETAILS
        // ==========================================

        /*
         * Always use the status stored against
         * the selected version.
         *
         * This controls:
         * Digital Copy
         * Order Received
         * Partially Received
         * Completed
         * Under Billing
         * Payment Pending
         */
        status:
            selectedVersion?.po?.status ||
            selectedVersion?.status ||
            printHistory?.status ||
            "",

        poNumber:
            selectedVersion?.po?.poNumber ||
            printHistory?.poNumber ||
            "",

        createdAt:
            selectedVersion?.po?.createdAt ||
            selectedVersion?.createdAt ||
            printHistory?.createdAt ||
            null,

        poType:
            selectedVersion?.po?.poType ||
            printHistory?.poType ||
            "",

        subject:
            selectedVersion?.po?.subject ||
            printHistory?.subject ||
            "",

        reference:
            selectedVersion?.po?.reference ||
            printHistory?.reference ||
            "",

        termCondition:
            Array.isArray(
                selectedVersion?.po?.termCondition
            )
                ? selectedVersion.po.termCondition
                : Array.isArray(
                    printHistory?.termCondition
                )
                    ? printHistory.termCondition
                    : [],

        items:
            Array.isArray(
                selectedVersion?.po?.items
            )
                ? selectedVersion.po.items
                : Array.isArray(
                    printHistory?.items
                )
                    ? printHistory.items
                    : [],

        // ==========================================
        // COMMON POPULATED REFERENCES
        // ==========================================

        requisitionNo:
            selectedVersion?.po?.requisitionNo ||
            printHistory?.requisitionNo ||
            null,

        history:
            Array.isArray(
                selectedVersion?.po?.history
            )
                ? selectedVersion.po.history
                : Array.isArray(
                    printHistory?.history
                )
                    ? printHistory.history
                    : [],

        createdBy:
            selectedVersion?.po?.createdBy ||
            printHistory?.createdBy ||
            null,

        deliveryAddress:
            selectedVersion?.po?.deliveryAddress ||
            printHistory?.deliveryAddress ||
            null,

        // ==========================================
        // PRINT VERSION INFORMATION
        // ==========================================

        printVersion: {
            revisionNo:
                Number(
                    selectedVersion?.revisionNo || 0
                ),

            revisionLabel:
                selectedVersion?.revisionLabel ||
                "",

            documentLabel:
                selectedVersion?.documentLabel ||
                "",

            isOriginal:
                Boolean(
                    selectedVersion?.isOriginal
                ),

            isCurrent:
                Boolean(
                    selectedVersion?.isCurrent
                ),

            isSuperseded:
                Boolean(
                    selectedVersion?.isSuperseded
                ),

            requestNumber:
                selectedVersion?.requestNumber ||
                "",

            remark:
                selectedVersion?.remark ||
                "",

            approvalRemark:
                selectedVersion?.approvalRemark ||
                "",

            approvedBy:
                selectedVersion?.approvedBy ||
                null,

            approvedAt:
                selectedVersion?.approvedAt ||
                null,

            createdAt:
                selectedVersion?.createdAt ||
                null
        }
    };

    // ==========================================
    // BUTTON LABEL
    // Latest revision is shown as Current PO
    // ==========================================

    const getVersionButtonLabel = (
        version
    ) => {
        const revisionNo =
            Number(
                version?.revisionNo || 0
            );

        if (revisionNo === 0) {
            return "Original PO";
        }

        if (version?.isCurrent) {
            return "Current PO";
        }

        return `Revision ${revisionNo}`;
    };

    return (
        <>
            {/* ==========================================
                VERSION SELECTOR
            ========================================== */}

            <div
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1200,
                    padding: "12px 15px",
                    backgroundColor: isDark ? "#2A2A2A" : "#f8f9fa",
                    borderBottom:
                        "1px solid #dee2e6"
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "8px"
                    }}
                >
                    <strong
                        style={{
                            marginRight: "4px"
                        }}
                    >
                        Select PO Version:
                    </strong>

                    {versions.map(
                        (version) => {
                            const revisionNo =
                                Number(
                                    version
                                        ?.revisionNo ||
                                    0
                                );

                            const isActive =
                                Number(
                                    selectedRevisionNo
                                ) ===
                                revisionNo;

                            return (
                                <button
                                    type="button"
                                    key={
                                        `${revisionNo}-${version?.documentLabel || ""}`
                                    }
                                    onClick={() =>
                                        setSelectedRevisionNo(
                                            revisionNo
                                        )
                                    }
                                    style={{
                                        padding:
                                            "6px 12px",

                                        border:
                                            isActive
                                                ? "1px solid #0d6efd"
                                                : "1px solid #ced4da",

                                        backgroundColor:
                                            isActive
                                                ? "#0d6efd"
                                                : "#ffffff",

                                        color:
                                            isActive
                                                ? "#ffffff"
                                                : "#212529",

                                        borderRadius:
                                            "4px",

                                        cursor:
                                            "pointer",

                                        fontSize:
                                            "12px",

                                        fontWeight:
                                            isActive
                                                ? "600"
                                                : "400"
                                    }}
                                >
                                    {getVersionButtonLabel(
                                        version
                                    )}
                                </button>
                            );
                        }
                    )}
                </div>

                {/* ==========================================
                    SELECTED VERSION INFO
                ========================================== */}

                <div
                    style={{
                        marginTop: "8px",
                        padding: "7px 10px",
                        border:
                            "1px solid #e9ecef",
                        borderRadius: "4px",
                        backgroundColor: isDark ? "#2A2A2A" : "#f8f9fa",
                        fontSize: "12px"
                    }}
                >
                    <strong>
                        Showing:
                    </strong>{" "}

                    {getVersionButtonLabel(
                        selectedVersion
                    )}

                    {selectedVersion
                        ?.isCurrent && (
                            <span
                                style={{
                                    marginLeft: "8px",
                                    color: "#198754",
                                    fontWeight: "600"
                                }}
                            >
                                Current Active PO
                            </span>
                        )}

                    {selectedVersion
                        ?.isSuperseded && (
                            <span
                                style={{
                                    marginLeft: "8px",
                                    color: "#6c757d",
                                    fontWeight: "600"
                                }}
                            >
                                Superseded
                            </span>
                        )}

                    {selectedVersion
                        ?.requestNumber && (
                            <span
                                style={{
                                    marginLeft: "12px"
                                }}
                            >
                                <strong>
                                    Request:
                                </strong>{" "}
                                {
                                    selectedVersion
                                        .requestNumber
                                }
                            </span>
                        )}

                    {selectedVersion
                        ?.remark && (
                            <div
                                style={{
                                    marginTop: "4px"
                                }}
                            >
                                <strong>
                                    Revision Remark:
                                </strong>{" "}
                                {
                                    selectedVersion
                                        .remark
                                }
                            </div>
                        )}

                    {selectedVersion
                        ?.approvedBy?.name && (
                            <div
                                style={{
                                    marginTop: "4px"
                                }}
                            >
                                <strong>
                                    Approved By:
                                </strong>{" "}
                                {
                                    selectedVersion
                                        .approvedBy
                                        .name
                                }
                            </div>
                        )}
                </div>
            </div>

            {/* ==========================================
                EXISTING PO PRINT TEMPLATE
            ========================================== */}

            <PoPrintTemplate
                item={selectedPo}
            />
        </>
    );
};

export default PoRevisionPrintTemplate;